/**
 * Activities API Hooks
 *
 * React Query hooks for fetching and managing activities from the backend.
 * Includes support for:
 * - Paginated listing with filters (audience, date range, read status, keyword)
 * - Marking activities as read with optimistic updates
 * - Unread count for badges (with polling)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    fetchActivities,
    fetchActivitiesByExpedition,
    markActivityAsRead,
    markActivityAsUnread,
    markActivitiesAsRead,
    markAllActivitiesAsRead,
    fetchUnreadCount,
    type BackendActivity,
    type ActivitiesFilters,
    type ActivitiesListResponse,
    type MarkAllReadResponse,
    type MarkReadResponse,
} from "@/data/api/activities";
import type { ActivityAudience } from "@/types/activity";
import type { ActivityItem } from "@/types/activity";
import { mapActivitiesToFrontend } from "@/utils/activity-mapper";
import { SYSTEM_ACTIVITY_CATEGORIES } from "@/utils/activity-labels";
import { ATTENTION_REQUIRED_QUERY_KEYS } from "@/hooks/api/use-attention-required";

/** Optimistic patch for activity list responses (feed + attention-required) */
function patchActivityListReadState(
    old: ActivitiesListResponse | undefined,
    activityId: string,
    markAsRead: boolean,
): ActivitiesListResponse | undefined {
    if (!old) return old;
    return {
        ...old,
        unread_count: markAsRead
            ? Math.max(0, old.unread_count - 1)
            : old.unread_count + 1,
        activities: old.activities.map((activity) =>
            activity.activity_id === activityId
                ? {
                      ...activity,
                      is_read: markAsRead,
                      read_at: markAsRead ? new Date().toISOString() : null,
                  }
                : activity,
        ),
    };
}

// ============================================================================
// Query Keys
// ============================================================================

export const ACTIVITIES_QUERY_KEYS = {
    all: ["activities"] as const,
    lists: () => [...ACTIVITIES_QUERY_KEYS.all, "list"] as const,
    list: (filters?: ActivitiesFilters) =>
        [...ACTIVITIES_QUERY_KEYS.lists(), filters] as const,
    unreadCount: (audience: string) =>
        [...ACTIVITIES_QUERY_KEYS.all, "unread-count", audience] as const,
    details: () => [...ACTIVITIES_QUERY_KEYS.all, "detail"] as const,
    detail: (id: string) => [...ACTIVITIES_QUERY_KEYS.details(), id] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch activities with filters and pagination
 *
 * @param audience - The audience to filter by (shipper, receiver, freightForwarder)
 * @param filters - Additional filters (is_read, from_date, to_date, keyword)
 * @param options - Additional React Query options
 *
 * @example
 * const { data, isLoading, error } = useActivities("shipper", {
 *   is_read: false,
 *   limit: 20
 * });
 */
export function useActivities(
    audience: ActivityAudience | string,
    filters?: Omit<ActivitiesFilters, "audience">,
    options?: {
        enabled?: boolean;
        refetchInterval?: number | false;
    }
) {
    const fullFilters: ActivitiesFilters = {
        ...filters,
        audience,
    };

    return useQuery({
        queryKey: ACTIVITIES_QUERY_KEYS.list(fullFilters),
        queryFn: async (): Promise<ActivitiesListResponse> => {
            return await fetchActivities(fullFilters);
        },
        enabled: options?.enabled ?? true,
        // Default polling every 30 seconds for real-time updates
        refetchInterval: options?.refetchInterval ?? 30000,
        // Keep previous data while fetching new data
        placeholderData: (previousData) => previousData,
    });
}

const LIMIT_PER_CATEGORY = 50;

/**
 * Fetches system activities per category with pagination (50 per category).
 * One API call per category; load more per category when user reaches end of that carousel.
 *
 * @param audience - shipper, receiver, freightForwarder
 * @param filters - from_date, to_date, keyword, readState (is_read)
 * @param options - enabled, refetchInterval
 */
export function useActivitiesByCategory(
    audience: ActivityAudience | string,
    filters: {
        readState?: "all" | "read" | "unread";
        fromDate?: Date | null;
        toDate?: Date | null;
        keyword?: string;
    },
    options?: {
        enabled?: boolean;
        refetchInterval?: number | false;
    }
) {
    const categories = SYSTEM_ACTIVITY_CATEGORIES;
    const [skipByCategory, setSkipByCategory] = useState<Record<string, number>>(() =>
        Object.fromEntries(categories.map((c) => [c, 0]))
    );
    const [accumulatedByCategory, setAccumulatedByCategory] = useState<Record<string, ActivityItem[]>>(
        () => Object.fromEntries(categories.map((c) => [c, []]))
    );
    const [rawActivitiesMap, setRawActivitiesMap] = useState<Record<string, BackendActivity>>({});

    const baseFilters = useMemo(
        () => ({
            audience,
            is_read:
                filters.readState === "all"
                    ? undefined
                    : filters.readState === "read",
            from_date: filters.fromDate?.toISOString(),
            to_date: filters.toDate?.toISOString(),
            keyword: filters.keyword,
            limit: LIMIT_PER_CATEGORY,
        }),
        [audience, filters.readState, filters.fromDate, filters.toDate, filters.keyword]
    );

    useEffect(() => {
        setSkipByCategory(() =>
            Object.fromEntries(categories.map((c) => [c, 0]))
        );
        setAccumulatedByCategory(() =>
            Object.fromEntries(categories.map((c) => [c, []]))
        );
        setRawActivitiesMap({});
    }, [
        filters.readState,
        filters.fromDate?.getTime(),
        filters.toDate?.getTime(),
        filters.keyword,
    ]);

    const queryResults = useQueries({
        queries: categories.map((cat) => {
            const skip = skipByCategory[cat];
            const fullFilters: ActivitiesFilters = {
                ...baseFilters,
                activity_category: cat,
                skip,
            };
            return {
                queryKey: ACTIVITIES_QUERY_KEYS.list(fullFilters),
                queryFn: async (): Promise<ActivitiesListResponse> => {
                    return await fetchActivities(fullFilters);
                },
                enabled: (options?.enabled ?? true),
                refetchInterval: options?.refetchInterval ?? 30000,
                placeholderData: (previousData: ActivitiesListResponse | undefined) =>
                    previousData,
            };
        }),
    });

    useEffect(() => {
        categories.forEach((cat, i) => {
            const result = queryResults[i];
            const data = result?.data;
            if (!data?.activities) return;
            const skip = skipByCategory[cat];
            const mapped = mapActivitiesToFrontend(data.activities);
            setAccumulatedByCategory((prev) => ({
                ...prev,
                [cat]:
                    skip === 0 ? mapped : [...(prev[cat] ?? []), ...mapped],
            }));
            // Store raw activities by ID for ExpandableActivityCard
            // Always merge — the filter-reset effect above handles clearing
            setRawActivitiesMap((prev) => {
                const next = { ...prev };
                for (const raw of data.activities) {
                    next[raw.activity_id] = raw;
                }
                return next;
            });
        });
    }, [
        queryResults.map((r) => r.dataUpdatedAt ?? 0).join(","),
        Object.values(skipByCategory).join(","),
    ]);

    const items = useMemo(() => {
        const merged: ActivityItem[] = [];
        categories.forEach((cat) => {
            merged.push(...(accumulatedByCategory[cat] ?? []));
        });
        merged.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        return merged;
    }, [accumulatedByCategory, categories]);

    const hasMoreForCategory = useCallback(
        (category: string) => {
            const i = categories.indexOf(category);
            if (i < 0) return false;
            const result = queryResults[i];
            const data = result?.data;
            if (!data) return false;
            return data.activities.length === LIMIT_PER_CATEGORY;
        },
        [queryResults, accumulatedByCategory, categories]
    );

    const onLoadMoreForCategory = useCallback((category: string) => {
        setSkipByCategory((prev) => ({
            ...prev,
            [category]: (prev[category] ?? 0) + LIMIT_PER_CATEGORY,
        }));
    }, []);

    const isLoadingMoreForCategory = useCallback(
        (category: string) => {
            const i = categories.indexOf(category);
            if (i < 0) return false;
            const result = queryResults[i];
            const skip = skipByCategory[category] ?? 0;
            return skip > 0 && (result?.isFetching ?? false);
        },
        [queryResults, skipByCategory, categories]
    );

    const isLoading = queryResults.some((r) => r.isLoading && !r.data);
    const isError = queryResults.some((r) => r.isError);
    const error = queryResults.find((r) => r.error)?.error;

    const totalFromApi = queryResults.reduce(
        (sum, r) => sum + (r.data?.total ?? 0),
        0
    );

    return {
        items,
        rawActivitiesMap,
        isLoading,
        isError,
        error,
        totalFromApi,
        hasMoreForCategory,
        onLoadMoreForCategory,
        isLoadingMoreForCategory,
    };
}

/**
 * Hook to fetch unread activity count for a specific audience
 * Useful for showing badges in navigation
 *
 * @param audience - The audience to filter by
 * @param options - Additional React Query options
 *
 * @example
 * const { data: unreadCount } = useActivityUnreadCount("shipper");
 * // Returns: 5
 */
export function useActivityUnreadCount(
    audience: ActivityAudience | string,
    options?: {
        enabled?: boolean;
        refetchInterval?: number | false;
    }
) {
    return useQuery({
        queryKey: ACTIVITIES_QUERY_KEYS.unreadCount(audience),
        queryFn: async (): Promise<number> => {
            return await fetchUnreadCount(audience);
        },
        enabled: options?.enabled ?? true,
        // Poll every 60 seconds for badge updates
        refetchInterval: options?.refetchInterval ?? 60000,
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to mark an activity as read/reviewed
 * Includes optimistic update for immediate UI feedback
 *
 * @example
 * const { mutate: markAsRead } = useMarkActivityAsRead();
 * markAsRead({ activityId: "123", notes: "Reviewed by user" });
 */
export function useMarkActivityAsRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            activityId,
        }: {
            activityId: string;
        }): Promise<MarkReadResponse> => {
            return await markActivityAsRead(activityId);
        },
        // Optimistic update: immediately update the UI
        onMutate: async ({ activityId }) => {
            await queryClient.cancelQueries({
                queryKey: ACTIVITIES_QUERY_KEYS.lists(),
            });
            await queryClient.cancelQueries({
                queryKey: ATTENTION_REQUIRED_QUERY_KEYS.all,
            });

            const previousLists = queryClient.getQueriesData({
                queryKey: ACTIVITIES_QUERY_KEYS.lists(),
            });
            const previousAttention = queryClient.getQueriesData({
                queryKey: ATTENTION_REQUIRED_QUERY_KEYS.all,
            });

            queryClient.setQueriesData(
                { queryKey: ACTIVITIES_QUERY_KEYS.lists() },
                (old) =>
                    patchActivityListReadState(
                        old as ActivitiesListResponse | undefined,
                        activityId,
                        true,
                    ),
            );
            queryClient.setQueriesData(
                { queryKey: ATTENTION_REQUIRED_QUERY_KEYS.all },
                (old) =>
                    patchActivityListReadState(
                        old as ActivitiesListResponse | undefined,
                        activityId,
                        true,
                    ),
            );

            return { previousLists, previousAttention };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousLists) {
                context.previousLists.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            if (context?.previousAttention) {
                context.previousAttention.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ACTIVITIES_QUERY_KEYS.lists(),
            });
            queryClient.invalidateQueries({
                queryKey: ATTENTION_REQUIRED_QUERY_KEYS.all,
            });
            queryClient.invalidateQueries({
                queryKey: ACTIVITIES_QUERY_KEYS.all,
                predicate: (query) =>
                    query.queryKey.includes("unread-count"),
            });
            queryClient.invalidateQueries({ queryKey: ['email', 'counts'] });
        },
    });
}

/**
 * Hook to mark an activity as unread (reverse of mark as read/reviewed)
 * Includes optimistic update for immediate UI feedback
 */
export function useMarkActivityAsUnread() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            activityId,
        }: {
            activityId: string;
        }): Promise<MarkReadResponse> => {
            return await markActivityAsUnread(activityId);
        },
        onMutate: async ({ activityId }) => {
            await queryClient.cancelQueries({
                queryKey: ACTIVITIES_QUERY_KEYS.lists(),
            });
            await queryClient.cancelQueries({
                queryKey: ATTENTION_REQUIRED_QUERY_KEYS.all,
            });

            const previousLists = queryClient.getQueriesData({
                queryKey: ACTIVITIES_QUERY_KEYS.lists(),
            });
            const previousAttention = queryClient.getQueriesData({
                queryKey: ATTENTION_REQUIRED_QUERY_KEYS.all,
            });

            queryClient.setQueriesData(
                { queryKey: ACTIVITIES_QUERY_KEYS.lists() },
                (old) =>
                    patchActivityListReadState(
                        old as ActivitiesListResponse | undefined,
                        activityId,
                        false,
                    ),
            );
            queryClient.setQueriesData(
                { queryKey: ATTENTION_REQUIRED_QUERY_KEYS.all },
                (old) =>
                    patchActivityListReadState(
                        old as ActivitiesListResponse | undefined,
                        activityId,
                        false,
                    ),
            );

            return { previousLists, previousAttention };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousLists) {
                context.previousLists.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            if (context?.previousAttention) {
                context.previousAttention.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ACTIVITIES_QUERY_KEYS.lists(),
            });
            queryClient.invalidateQueries({
                queryKey: ATTENTION_REQUIRED_QUERY_KEYS.all,
            });
            queryClient.invalidateQueries({
                queryKey: ACTIVITIES_QUERY_KEYS.all,
                predicate: (query) =>
                    query.queryKey.includes("unread-count"),
            });
            queryClient.invalidateQueries({ queryKey: ['email', 'counts'] });
        },
    });
}

/**
 * Hook to mark multiple activities as read
 * Useful for "mark all as read" functionality
 *
 * @example
 * const { mutate: markAllAsRead } = useMarkMultipleActivitiesAsRead();
 * markAllAsRead({ activityIds: ["123", "456", "789"] });
 */
export function useMarkMultipleActivitiesAsRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            activityIds,
        }: {
            activityIds: string[];
        }): Promise<MarkReadResponse> => {
            // Use bulk mark-as-read endpoint
            return await markActivitiesAsRead(activityIds);
        },
        onSuccess: () => {
            // Invalidate all activity queries
            queryClient.invalidateQueries({
                queryKey: ACTIVITIES_QUERY_KEYS.all,
            });
            // Refresh email counts (marking email activities as read affects inbox_unread count)
            queryClient.invalidateQueries({ queryKey: ['email', 'counts'] });
        },
    });
}

/**
 * Hook to mark ALL unread activities as read (system + email) for an audience.
 * Pass the audience (e.g. "shipper") so that both system logs and trade comms are updated.
 *
 * @param audience - Optional audience to limit to (e.g. "shipper"). If omitted, marks all.
 * @example
 * const { mutate: markAllAsRead } = useMarkAllActivitiesAsRead("shipper");
 * markAllAsRead();
 */
export function useMarkAllActivitiesAsRead(audience?: string) {
    const queryClient = useQueryClient();

    return useMutation<MarkAllReadResponse, Error, void>({
        mutationFn: async () => {
            return await markAllActivitiesAsRead(audience);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ACTIVITIES_QUERY_KEYS.all,
            });
            queryClient.invalidateQueries({ queryKey: ['email', 'counts'] });
        },
    });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook to prefetch activities for a given audience
 * Useful for prefetching on hover or navigation intent
 *
 * @example
 * const prefetchActivities = usePrefetchActivities();
 * onMouseEnter={() => prefetchActivities("shipper")}
 */
export function usePrefetchActivities() {
    const queryClient = useQueryClient();

    return (audience: ActivityAudience | string, filters?: ActivitiesFilters) => {
        const fullFilters: ActivitiesFilters = {
            ...filters,
            audience,
        };

        queryClient.prefetchQuery({
            queryKey: ACTIVITIES_QUERY_KEYS.list(fullFilters),
            queryFn: () => fetchActivities(fullFilters),
            staleTime: 30000, // Consider fresh for 30 seconds
        });
    };
}

/**
 * Hook to invalidate all activity queries
 * Useful when activities might have changed externally
 *
 * @example
 * const invalidateActivities = useInvalidateActivities();
 * onClick={() => invalidateActivities()}
 */
export function useInvalidateActivities() {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries({
            queryKey: ACTIVITIES_QUERY_KEYS.all,
        });
    };
}

// ============================================================================
// Container/Expedition Activities Hook
// ============================================================================

/**
 * Hook to fetch activities for a specific container/expedition
 *
 * @param containerId - The container/expedition UUID
 * @param limit - Maximum number of activities to return (default: 50)
 * @param options - Additional React Query options
 *
 * @example
 * const { data, isLoading, error } = useContainerActivities("uuid-here");
 */
export function useContainerActivities(
    containerId: string | undefined,
    limit: number = 50,
    options?: {
        enabled?: boolean;
        refetchInterval?: number | false;
    }
) {
    return useQuery({
        queryKey: [...ACTIVITIES_QUERY_KEYS.all, "container", containerId, limit],
        queryFn: async (): Promise<ActivitiesListResponse> => {
            if (!containerId) {
                return { activities: [], total: 0, unread_count: 0, attention_count: 0, action_needed_count: 0, require_review_count: 0 };
            }
            const { fetchActivitiesByExpedition } = await import("@/data/api/activities");
            return await fetchActivitiesByExpedition(containerId, limit);
        },
        enabled: (options?.enabled ?? true) && !!containerId,
        // Default polling every 60 seconds for updates
        refetchInterval: options?.refetchInterval ?? 60000,
        // Keep previous data while fetching new data
        placeholderData: (previousData) => previousData,
    });
}

const PAGE_SIZE = 20;

/**
 * Paginated hook for container activities with incremental load-more.
 * Page 0 polls every 60s for live updates; subsequent pages are static.
 * Deduplicates by activity_id on append.
 *
 * @param containerId - The container/expedition UUID
 * @param options - Additional React Query options
 */
export function useContainerActivitiesPaginated(
    containerId: string | undefined,
    options?: {
        enabled?: boolean;
    }
) {
    const [skip, setSkip] = useState(0);
    const [accumulated, setAccumulated] = useState<BackendActivity[]>([]);
    const [total, setTotal] = useState(0);
    const [attentionCount, setAttentionCount] = useState(0);
    const [actionNeededCount, setActionNeededCount] = useState(0);
    const [requireReviewCount, setRequireReviewCount] = useState(0);
    const prevContainerIdRef = useRef(containerId);

    // Reset when containerId changes
    useEffect(() => {
        if (prevContainerIdRef.current !== containerId) {
            setSkip(0);
            setAccumulated([]);
            setTotal(0);
            setAttentionCount(0);
            setActionNeededCount(0);
            setRequireReviewCount(0);
            prevContainerIdRef.current = containerId;
        }
    }, [containerId]);

    // Page 0 query — polls for live updates
    const page0 = useQuery({
        queryKey: [...ACTIVITIES_QUERY_KEYS.all, "container-paginated", containerId, 0, PAGE_SIZE],
        queryFn: async (): Promise<ActivitiesListResponse> => {
            if (!containerId) {
                return { activities: [], total: 0, unread_count: 0, attention_count: 0, action_needed_count: 0, require_review_count: 0 };
            }
            return await fetchActivitiesByExpedition(containerId, PAGE_SIZE, 0);
        },
        enabled: (options?.enabled ?? true) && !!containerId,
        refetchInterval: 60000,
        placeholderData: (prev) => prev,
    });

    // Subsequent page query — only runs when skip > 0
    const nextPage = useQuery({
        queryKey: [...ACTIVITIES_QUERY_KEYS.all, "container-paginated", containerId, skip, PAGE_SIZE],
        queryFn: async (): Promise<ActivitiesListResponse> => {
            if (!containerId) {
                return { activities: [], total: 0, unread_count: 0, attention_count: 0, action_needed_count: 0, require_review_count: 0 };
            }
            return await fetchActivitiesByExpedition(containerId, PAGE_SIZE, skip);
        },
        enabled: (options?.enabled ?? true) && !!containerId && skip > 0,
        placeholderData: (prev) => prev,
    });

    // Merge page 0 data into accumulated (replaces first PAGE_SIZE entries)
    useEffect(() => {
        if (!page0.data) return;
        setTotal(page0.data.total);
        setAttentionCount(page0.data.attention_count);
        setActionNeededCount(page0.data.action_needed_count);
        setRequireReviewCount(page0.data.require_review_count);

        setAccumulated((prev) => {
            const page0Ids = new Set(page0.data.activities.map((a) => a.activity_id));
            // Keep any activities beyond page 0 that aren't duplicated
            const beyond = prev.filter((a) => !page0Ids.has(a.activity_id));
            // Page0 activities are most recent, then older accumulated
            const page0Activities = page0.data.activities;
            // Dedup beyond against page0
            const seenIds = new Set(page0Activities.map((a) => a.activity_id));
            const uniqueBeyond = beyond.filter((a) => {
                if (seenIds.has(a.activity_id)) return false;
                seenIds.add(a.activity_id);
                return true;
            });
            return [...page0Activities, ...uniqueBeyond];
        });
    }, [page0.data]);

    // Append next page data
    useEffect(() => {
        if (!nextPage.data || skip === 0) return;

        setAccumulated((prev) => {
            const existingIds = new Set(prev.map((a) => a.activity_id));
            const newActivities = nextPage.data.activities.filter(
                (a) => !existingIds.has(a.activity_id)
            );
            return [...prev, ...newActivities];
        });
    }, [nextPage.data, skip]);

    const hasMore = accumulated.length < total;
    const isLoadingMore = skip > 0 && nextPage.isFetching;

    const loadMore = useCallback(() => {
        if (!hasMore || isLoadingMore) return;
        setSkip(accumulated.length);
    }, [hasMore, isLoadingMore, accumulated.length]);

    return {
        activities: accumulated,
        total,
        attention_count: attentionCount,
        action_needed_count: actionNeededCount,
        require_review_count: requireReviewCount,
        isLoading: page0.isLoading,
        isLoadingMore,
        error: page0.error,
        hasMore,
        loadMore,
    };
}
